'use client';

/**
 * ModalContext — global modal system
 *
 * Provides a single, app-wide modal slot rendered as a React portal directly
 * into <body>. Only one modal can be open at a time; opening a new one
 * replaces the previous content.
 *
 * Tree structure:
 *   ModalProvider (wraps the app)
 *     └─ ModalBackdrop  (portal → document.body, rendered when modal is set)
 *          └─ {modal}   (any ReactNode passed via setModal)
 *
 * Usage: consume via the `useModal` hook — never import ModalContext directly.
 */

import { createContext, useCallback, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import ModalBackdrop from '../../components/ui/Modal/ModalBackdrop';

interface ModalContextType {
  /** Open a modal by passing any ReactNode, or close it by passing null. */
  setModal: (modal: ReactNode | null) => void;
}

export const ModalContext = createContext<ModalContextType | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

/**
 * Wrap the application (or a subtree) with ModalProvider to enable the modal
 * system. Typically placed near the root layout alongside other providers.
 */
export function ModalProvider({ children }: ModalProviderProps) {
  const [modal, setModal] = useState<ReactNode | null>(null);

  /**
   * Deferred state update via setTimeout(0) so that any click-triggered
   * open/close runs after the current event loop tick, avoiding race
   * conditions with backdrop click handlers and CSS transitions.
   */
  const handleSetModal = useCallback((modal: ReactNode | null = null) => {
    const id = setTimeout(() => {
      setModal(modal);
      clearTimeout(id);
    }, 0);
  }, []);

  return (
    <ModalContext.Provider value={{ setModal: handleSetModal }}>
      {children}
      {/*
       * Portal renders outside the normal React tree so the modal is never
       * clipped by overflow:hidden or z-index stacking contexts on ancestors.
       * The SSR guard (typeof window) prevents a hydration mismatch.
       */}
      {modal &&
        typeof window !== 'undefined' &&
        createPortal(
          <ModalBackdrop onClose={handleSetModal}>{modal}</ModalBackdrop>,
          document.body
        )}
    </ModalContext.Provider>
  );
}
